import styled from 'styled-components';

import { Button } from '@components/button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconHistory } from '@components/icons/history';
import { IconReplace } from '@components/icons/replace';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { PublishedVersion } from 'api/types';

export type PreviewModeHeaderProps = {
  version: PublishedVersion;
  onClose: () => void;
  onOpenVersionHistory?: () => void;
  onRestore?: () => void;
};

export const PreviewModeHeader = ({
  version,
  onClose,
  onOpenVersionHistory,
  onRestore,
}: PreviewModeHeaderProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <PreviewModeHeaderContainer>
      <Stack orientation="horizontal" space={4} align="center">
        <Button
          variant="secondary"
          iconLeft={<IconHistory />}
          text={t('versionHistory')}
          onClick={onOpenVersionHistory}
        />
        <Separator orientation="horizontal" style={{ height: pxToRem(24) }} />
        <Stack orientation="horizontal" space={2} align="center">
          <div>
            <TextStatusIndicator variant={'success'} text={t('published')} />
            <Text
              font="regular14"
              color="textHelper"
              text={abbreviateDate(version.dateCreated, 'en', 'datetime')}
            />
          </div>
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} align="center">
        <Text color="textHelper" text={t('viewOnly')} />
        <Button
          variant="primary"
          iconLeft={<IconReplace />}
          onClick={onRestore}
          text={t('restoreThisVersion')}
        />
        <Button
          variant="secondary"
          iconLeft={<IconArrowLeft />}
          onClick={onClose}
          text={t('backToCurrentVersion')}
        />
      </Stack>
    </PreviewModeHeaderContainer>
  );
};

const PreviewModeHeaderContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 6,
  justify: 'space-between',
  align: 'center',
})({
  padding: pxToRem(24),
  background: theme.color.backgroundLightDark,
  borderBottom: `1px solid ${theme.color.border10}`,
});

const locale = {
  en: {
    versionHistory: 'Version history',
    published: 'Published',
    viewOnly: 'View only',
    restoreThisVersion: 'Restore this version',
    backToCurrentVersion: 'Back to current version',
  },
};
